import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import {
  CheckCircle2,
  Circle,
  RotateCcw,
  ArrowRight,
  Gauge,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import type {
  FinancialsSetupResponse,
  SetupStepKey,
  SetupStepState,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { useAuth } from '../../lib/authContext';
import { ProfileLockedCard } from '../../components/ProfileLockedCard';
import { CalculationBasisTrigger } from '../../components/financials/CalculationBasisDrawer';
import { SustainabilityCard } from '../../components/financials/SustainabilityCard';

/**
 * SIL-302 (W4, resources/plans/financials-hifi-flow-spec.md slice 1 "Setup
 * Guide"). Six-step "path to a hold-or-sell decision" checklist consuming
 * `GET /api/client/financials/setup` (SIL-293). Always directly addressable
 * regardless of completion — the redirect logic lives ONLY in
 * `/financials/index.tsx`'s `beforeLoad`, never here.
 *
 * Setup completeness is UX only — it never gates data or authorization
 * (spec slice 1); this screen has no server-side dependency beyond the read
 * + the two persisted acks (confirm-policy admin-only, mark-reviewed any
 * member).
 */
export const Route = createFileRoute('/financials/setup')({
  component: SetupGuidePage,
});

// ---------------------------------------------------------------------------
// Per-step copy — labels, "why this matters", and the action a click takes.
// Kept beside the page (not shared) since it's pure display copy, same
// convention as BASIS_SOURCE_LABELS living beside CalculationBasisDrawer.
// ---------------------------------------------------------------------------

interface StepCopy {
  title: string;
  why: string;
  /** Link target for the primary action button when the step is incomplete
   * or reopened. */
  to: string;
}

const STEP_COPY: Record<SetupStepKey, StepCopy> = {
  connect_monitoring: {
    title: 'Connect monitoring',
    why: 'Live telemetry keeps utilization and condition-grade signals current — without it, FMV and depreciation both fall back to rated-spec estimates instead of observed data.',
    to: '/integrations',
  },
  import_erp: {
    title: 'Import ERP financials',
    why: 'Purchase price and in-service date drive net book value. Without an ERP import, book value falls back to an estimate from the SKU launch list price.',
    to: '/integrations',
  },
  confirm_policy: {
    title: 'Confirm capitalization policy',
    why: 'Depreciation method, useful life, and salvage value determine how book value is calculated for every asset class. An admin has to attest to the policy before it can anchor a decision.',
    to: '/financials/policy',
  },
  review_book_vs_fmv: {
    title: 'Review Book vs FMV',
    why: 'Seeing where fair market value sits against net book value, by class, is the comparison every hold-or-sell decision starts from.',
    to: '/financials/book-vs-fmv',
  },
  model_scenario: {
    title: 'Model first scenario',
    why: 'A saved scenario turns the comparison into a concrete recovery estimate — proceeds, ITAD baseline, and net position for a specific cohort.',
    to: '/financials/scenarios',
  },
  act_draft_listing: {
    title: 'Act on a scenario',
    why: 'Drafting a listing from a scenario is the step that converts analysis into a market-facing action.',
    to: '/financials/scenarios',
  },
};

function stepActionLabel(step: SetupStepState): string {
  if (step.key === 'confirm_policy') {
    if (step.complete) return 'Edit';
    return step.reopened ? 'Re-confirm' : 'Confirm';
  }
  if (step.key === 'import_erp') return step.complete ? 'Re-import' : 'Import';
  if (step.key === 'review_book_vs_fmv') return 'Review';
  if (step.key === 'model_scenario') return step.complete ? 'Continue' : 'Model';
  if (step.key === 'act_draft_listing') return 'Open';
  // connect_monitoring
  return step.complete ? 'Manage' : 'Connect';
}

// ---------------------------------------------------------------------------
// Readiness meter
// ---------------------------------------------------------------------------

function ReadinessMeter({ data }: { data: FinancialsSetupResponse }) {
  const { readiness, basis } = data;
  return (
    <Card padding="md">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Gauge className="size-4 text-brand-600" />
          <p className="text-sm font-semibold text-zinc-900">Decision readiness</p>
          <CalculationBasisTrigger title="Decision readiness" basis={basis} />
        </div>
        <p className="text-lg font-semibold tabular-nums text-zinc-900">{readiness.score}%</p>
      </div>
      <p className="mt-1 text-xs text-zinc-500">
        A self-asserted composite of data coverage below — never an assurance artifact, and never
        exported (spec Cross-cutting).
      </p>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
        <div
          className="h-full rounded-full bg-brand-600 transition-[width]"
          style={{ width: `${readiness.score}%` }}
        />
      </div>
      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(
          [
            ['Financial-data coverage', readiness.pctAssetsWithFinancials],
            ['Live telemetry coverage', readiness.pctLiveTelemetry],
            ['Comps freshness', readiness.compsFreshnessPct],
          ] as const
        ).map(([label, pct]) => (
          <div key={label}>
            <dt className="text-xs text-zinc-500">{label}</dt>
            <dd className="text-sm font-medium tabular-nums text-zinc-800">{pct}%</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step row
// ---------------------------------------------------------------------------

function StepRow({
  step,
  isAdmin,
  confirming,
  onConfirmPolicy,
  onMarkReviewed,
}: {
  step: SetupStepState;
  isAdmin: boolean;
  confirming: boolean;
  onConfirmPolicy: () => void;
  onMarkReviewed: () => void;
}) {
  const copy = STEP_COPY[step.key];
  const isReopened = step.reopened === true;
  const done = step.complete;

  return (
    <li className="flex flex-col gap-3 border-b border-zinc-100 px-5 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {done ? (
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
        ) : isReopened ? (
          <RotateCcw className="mt-0.5 size-5 shrink-0 text-amber-500" />
        ) : (
          <Circle className="mt-0.5 size-5 shrink-0 text-zinc-300" />
        )}
        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-zinc-900">
            {step.step}. {copy.title}
            {isReopened && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                needs re-confirm
              </span>
            )}
          </p>
          <p className="mt-0.5 max-w-md text-xs text-zinc-500">{copy.why}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 pl-8 sm:pl-0">
        {step.key === 'confirm_policy' && isAdmin ? (
          <>
            <Link
              to={copy.to}
              className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              {done && !isReopened ? 'Edit policy' : 'Review policy'}
            </Link>
            {(!done || isReopened) && (
              <Button size="sm"
                disabled={confirming}
                onClick={onConfirmPolicy}
              >
                {confirming ? 'Confirming…' : 'Confirm'}
              </Button>
            )}
          </>
        ) : step.key === 'confirm_policy' ? (
          // Non-admin: server is the 403 boundary, but the confirm action is
          // hidden here too (matches policy.tsx's isAdmin pattern) — a
          // member can still view the policy, just not attest to it.
          <Link
            to={copy.to}
            className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            View policy
          </Link>
        ) : step.key === 'review_book_vs_fmv' ? (
          <Link
            to={copy.to}
            onClick={onMarkReviewed}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
          >
            {stepActionLabel(step)}
            <ArrowRight className="ml-1 inline size-3" />
          </Link>
        ) : (
          <Link
            to={copy.to}
            className={
              done
                ? 'rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50'
                : 'rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700'
            }
          >
            {stepActionLabel(step)}
            <ArrowRight className="ml-1 inline size-3" />
          </Link>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function SetupGuidePage() {
  const { canSell, sellerMissingFields, isLoading: authLoading, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const setupQ = useQuery<FinancialsSetupResponse>({
    queryKey: qk.financials.setup(),
    queryFn: async () => {
      const res = await api.api.client.financials.setup.$get();
      await throwIfNotOk(res);
      return res.json();
    },
    enabled: canSell,
  });

  async function confirmPolicy() {
    setConfirming(true);
    setConfirmError(null);
    try {
      const res = await api.api.client.financials.setup['confirm-policy'].$post();
      await throwIfNotOk(res);
      await queryClient.invalidateQueries({ queryKey: qk.financials.setup() });
    } catch (ex: unknown) {
      setConfirmError((ex as Error).message);
    } finally {
      setConfirming(false);
    }
  }

  // "Review Book vs FMV" is a persisted first-open ack (spec: any member).
  // Firing it from the setup guide's own "Review" click — rather than
  // instrumenting book-vs-fmv.tsx's mount, which several other W4 screens
  // touch in parallel — keeps the ack scoped to this file; best-effort
  // (fire-and-forget) since navigation must never block on it.
  function markReviewed() {
    api.api.client.financials.setup['mark-reviewed']
      .$post()
      .then((res) => throwIfNotOk(res))
      .then(() => queryClient.invalidateQueries({ queryKey: qk.financials.setup() }))
      .catch(() => {
        /* best-effort — a failed ack just means step 4 stays open next visit */
      });
  }

  if (authLoading) return <LoadingShell rows={3} />;
  if (!canSell) return <ProfileLockedCard side="seller" missingFields={sellerMissingFields} />;
  if (setupQ.isLoading) return <LoadingShell rows={5} />;
  if (setupQ.error || !setupQ.data) {
    return (
      <div className="space-y-2">
        <ErrorState
          title="Couldn't load the setup guide."
          error={setupQ.error}
          onRetry={() => void setupQ.refetch()}
        />
        <p className="text-center text-sm text-zinc-500">
          <Link to="/financials/book-vs-fmv" className="underline">
            Go to Book vs FMV
          </Link>{' '}
          instead.
        </p>
      </div>
    );
  }

  const data = setupQ.data;
  const allComplete = data.completedCount >= data.totalSteps;
  const anyReopened = data.steps.some((s) => s.reopened);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageHeader title="Financials Setup Guide" />
          <p className="mt-1 text-sm text-zinc-500">
            The path to a hold-or-sell decision — six steps from connected data to a drafted
            listing.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700">
          {allComplete ? (
            <ShieldCheck className="size-4 text-emerald-600" />
          ) : (
            <Sparkles className="size-4 text-brand-600" />
          )}
          {data.completedCount} of {data.totalSteps} complete
        </div>
      </div>

      {/* Returning-user microcopy — new hardware or a policy edit can reopen
          a step that was already done; explains why a returning user landed
          here instead of straight on Book vs FMV. */}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600">
        {anyReopened
          ? 'A step below needs a re-run — most often a capitalization-policy edit that postdates the last confirmation.'
          : 'New hardware, a policy edit, or a stale import can reopen a step here even after it was once complete — re-run any step below to keep your decision current.'}
      </div>

      <ReadinessMeter data={data} />

      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
        <ul>
          {data.steps.map((step) => (
            <StepRow
              key={step.key}
              step={step}
              isAdmin={isAdmin}
              confirming={confirming}
              onConfirmPolicy={confirmPolicy}
              onMarkReviewed={markReviewed}
            />
          ))}
        </ul>
      </div>

      {confirmError && <div className="text-sm text-red-500">{confirmError}</div>}

      {/* SIL-439 (W4) — sustainability setup extends this surface per
          resources/plans/sustainability-forecasting-spec.md §6 ("the setup
          card (targets, Cat 1/2 toggle, adder scalars) extends the existing
          Financials setup surface"). It sits below the six-step checklist:
          the steps are the path to a hold-or-sell decision, these are
          org-wide settings the carbon lens reads, not a seventh step. */}
      <SustainabilityCard isAdmin={isAdmin} />

      <p className="text-xs text-zinc-400">Data as of {new Date(data.generatedAt).toLocaleString()}.</p>
    </div>
  );
}
